/**
 * Ficha pública del partido: marcador, parciales, cómo se ganaron los puntos y
 * la evolución del marcador.
 *
 * ## El vivo de esta pantalla es POLLING, no SSE (ADR-020)
 *
 * Es la pantalla que puede tener miles de espectadores a la vez, y por eso NO
 * abre un socket: pide el snapshot cacheado del server cada `pollAfterMs` (5
 * segundos mientras el partido está en juego, 60 cuando terminó — el número lo
 * decide el server y viaja en el cuerpo). Con miles de espectadores el server
 * reconstruye el marcador 12 veces por minuto, no una por persona.
 *
 * El SSE está reservado para el overlay de transmisión (`CourtWidget`), que es
 * un consumidor y necesita el punto en pantalla al instante.
 *
 * ## Lo que NO hay acá, y es a propósito
 *
 * No hay planteles, no hay goleadores, no hay camisetas. La proyección pública
 * del API no los publica (remediación de la brecha B5 de
 * `docs/datos-personales.md`), así que esta pantalla no podría mostrarlos
 * aunque quisiera. Lo que sí muestra —cómo se ganó cada punto, agregado por
 * equipo— es información del partido sin autor.
 */
import { ScoreLineChart } from '../../components/Charts';
import { useT } from '../../i18n/use-t';
import { reasonLabel } from '../../lib/format';
import type {
  PublicClient,
  PublicMatchDetail,
  PublicMatchResponse,
  PublicScorePoint,
} from '../../lib/public-client';
import { publicCompetitionPath, publicHref, publicWidgetPath } from '../../lib/public-router';
import { usePolled } from './hooks';
import { EmptyBlock, formatDate, formatTime, interceptable, Section, SetPills } from './parts';
import { PublicState, Updated } from './PublicState';

export function PublicMatch({
  client,
  slug,
  base,
  matchId,
  onNavigate,
}: {
  client: PublicClient;
  slug: string;
  base: string;
  matchId: string;
  onNavigate: (href: string) => void;
}): JSX.Element {
  const t = useT();
  const state = usePolled<PublicMatchResponse>(() => client.getMatch(slug, matchId), [slug, matchId]);

  if (state.data === null) return <PublicState state={state} what={t('publicPortal.match')} />;

  return (
    <>
      <PublicMatchView data={state.data} slug={slug} base={base} onNavigate={onNavigate} />
      <Updated updatedAt={state.updatedAt} error={state.error} />
    </>
  );
}

export function PublicMatchView({
  data,
  slug,
  base,
  onNavigate,
}: {
  data: PublicMatchResponse;
  slug: string;
  base: string;
  onNavigate: (href: string) => void;
}): JSX.Element {
  const t = useT();
  const { fixture, match } = data;
  const matchId = fixture.matchId ?? '';

  if (match === null) {
    // El fixture lo tiene programado pero el log todavía no llegó: no es un
    // error, es un partido que no empezó.
    return (
      <>
        <h1 className="pub-title">
          {fixture.home?.name ?? t('publicPortal.tbd')} vs {fixture.away?.name ?? t('publicPortal.tbd')}
        </h1>
        <EmptyBlock>{t('publicPortal.notStarted')}</EmptyBlock>
      </>
    );
  }

  const competitionHref = publicHref(publicCompetitionPath(slug, fixture.competitionId), base);
  const live = match.status === 'live';

  return (
    <>
      <p className="pub-match-top" style={{ marginBottom: 6 }}>
        <a href={competitionHref} onClick={interceptable(competitionHref, onNavigate)}>
          {fixture.competitionName}
        </a>
        <span className="sep">·</span>
        <span>{t('publicPortal.matchday', { matchday: fixture.matchday })}</span>
        {fixture.scheduledAt !== null ? (
          <>
            <span className="sep">·</span>
            <span>
              {formatDate(fixture.scheduledAt)} {formatTime(fixture.scheduledAt)}
            </span>
          </>
        ) : null}
        {match.venue !== null ? (
          <>
            <span className="sep">·</span>
            <span>{match.venue}</span>
          </>
        ) : null}
        {live ? <span className="pub-chip live">{t('publicPortal.statusLive')}</span> : null}
      </p>

      <Scoreboard match={match} />

      <div className="pub-grid">
        <div className="pub-card">
          <h2>{t('publicPortal.pointsByReason')}</h2>
          <PointsByReason match={match} />
        </div>
        <div className="pub-card">
          <h2>{t('publicPortal.setsTable')}</h2>
          <SetsTable match={match} />
        </div>
      </div>

      <Section title={t('publicPortal.evolution')}>
        <Evolution match={match} />
      </Section>

      {matchId !== '' ? (
        <p className="pub-note">
          {t('publicPortal.widgetHint')}{' '}
          {/* Link normal, sin interceptar: el productor de la transmisión lo
              copia y lo pega en el browser source de OBS. */}
          <a href={publicHref(publicWidgetPath(slug, matchId), base)}>
            {t('publicPortal.widgetLink')}
          </a>
        </p>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Marcador
// ---------------------------------------------------------------------------

function Scoreboard({ match }: { match: PublicMatchDetail }): JSX.Element {
  const t = useT();
  return (
    <div className="pub-scoreboard">
      <div className="pub-score-row">
        <div className="pub-score-team">
          <h3>{match.home.name}</h3>
        </div>
        <div className="pub-score-sets">
          {match.home.setsWon} <span style={{ opacity: 0.4 }}>–</span> {match.away.setsWon}
        </div>
        <div className="pub-score-team away">
          <h3>{match.away.name}</h3>
        </div>
      </div>

      {match.current !== null ? (
        <p className="pub-score-current">
          {t('common.set', { set: match.current.set })}
          {': '}
          {match.current.serving === 'home' ? <span className="pub-serving" /> : null}
          {match.current.home} – {match.current.away}
          {match.current.serving === 'away' ? <span className="pub-serving" /> : null}
        </p>
      ) : null}

      <p style={{ textAlign: 'center', marginTop: 10, marginBottom: 0 }}>
        <SetPills sets={match.sets} />
      </p>
    </div>
  );
}

function SetsTable({ match }: { match: PublicMatchDetail }): JSX.Element {
  const t = useT();
  if (match.sets.length === 0) return <EmptyBlock>{t('publicPortal.noSets')}</EmptyBlock>;
  return (
    <table className="pub-sets-table">
      <thead>
        <tr>
          <th>{t('publicPortal.set')}</th>
          <th>{match.home.name}</th>
          <th>{match.away.name}</th>
        </tr>
      </thead>
      <tbody>
        {match.sets.map((set) => (
          <tr key={set.set}>
            <td>
              {set.set}
              {set.live ? ` · ${t('publicPortal.inPlay')}` : ''}
            </td>
            <td className={set.wonBy === 'home' ? 'strong' : undefined}>{set.home}</td>
            <td className={set.wonBy === 'away' ? 'strong' : undefined}>{set.away}</td>
          </tr>
        ))}
        <tr>
          <td>{t('publicPortal.total')}</td>
          <td className="strong">{match.home.points}</td>
          <td className="strong">{match.away.points}</td>
        </tr>
      </tbody>
    </table>
  );
}

/**
 * Cómo se ganaron los puntos. Es la única "estadística" del portal público, y
 * la que se puede publicar sin discusión: no tiene autor, es del EQUIPO.
 */
function PointsByReason({ match }: { match: PublicMatchDetail }): JSX.Element {
  const t = useT();
  const reasons = [
    ...new Set([...Object.keys(match.home.pointsByReason), ...Object.keys(match.away.pointsByReason)]),
  ].sort((a, b) => {
    const total = (r: string) =>
      (match.home.pointsByReason[r] ?? 0) + (match.away.pointsByReason[r] ?? 0);
    return total(b) - total(a);
  });

  if (reasons.length === 0) return <EmptyBlock>{t('publicPortal.noPoints')}</EmptyBlock>;

  return (
    <div className="pub-reasons">
      <span className="head">{t('publicPortal.reason')}</span>
      <span className="head num">{match.home.name}</span>
      <span className="head num">{match.away.name}</span>
      {reasons.map((reason) => (
        <PointsRow
          key={reason}
          label={reasonLabel(reason)}
          home={match.home.pointsByReason[reason] ?? 0}
          away={match.away.pointsByReason[reason] ?? 0}
        />
      ))}
    </div>
  );
}

function PointsRow({
  label,
  home,
  away,
}: {
  label: string;
  home: number;
  away: number;
}): JSX.Element {
  return (
    <>
      <span>{label}</span>
      <span className="num">{home}</span>
      <span className="num">{away}</span>
    </>
  );
}

// ---------------------------------------------------------------------------
// Evolución
// ---------------------------------------------------------------------------

/**
 * La "serpiente" del marcador, un gráfico por set. Se reusa el mismo
 * `ScoreLineChart` del portal interno: es el mismo dibujo del mismo dato, y
 * tener dos habría sido la garantía de que en algún momento muestren cosas
 * distintas.
 */
function Evolution({ match }: { match: PublicMatchDetail }): JSX.Element {
  const t = useT();
  if (match.progression.length === 0) return <EmptyBlock>{t('evolution.empty')}</EmptyBlock>;

  const bySet = new Map<number, PublicScorePoint[]>();
  for (const point of match.progression) {
    const list = bySet.get(point.set);
    if (list) list.push(point);
    else bySet.set(point.set, [point]);
  }

  return (
    <div className="pub-grid">
      {[...bySet.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([set, points]) => (
          <div className="pub-card" key={set}>
            <h2>{t('common.set', { set })}</h2>
            <ScoreLineChart
              // El 0-0 inicial se agrega acá (el API manda un punto por rally,
              // no el arranque): sin él la línea empieza en el primer punto y
              // parece que el set arrancó 1-0.
              points={[{ home: 0, away: 0 }, ...points]}
              homeName={match.home.name}
              awayName={match.away.name}
              set={set}
            />
          </div>
        ))}
    </div>
  );
}
